import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Languages } from 'lucide-react';
import i18n from '@/i18n/config';
import { LANGUAGES } from '@/i18n/languages';
import axios from '@/utils/axios';
import { useAuth } from '@/context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Language switcher for the site header and the mobile sheet menu.
 *
 * All of the hard parts already exist and are deliberately NOT re-implemented
 * here:
 *   - persistence     — `i18n/config.js` runs i18next-browser-languagedetector
 *                       with `caches: ['localStorage']`, so `changeLanguage()`
 *                       writes `wefaqna_language` itself and the same detector
 *                       reads it back first on the next load
 *                       (order: localStorage → navigator → htmlTag → 'ar').
 *   - <html dir/lang> — `applyDocumentDirection` is already subscribed to
 *                       i18next's `languageChanged` event in that same file.
 *   - dates           — `utils/dateLocale.js` reads `i18n.language` at call
 *                       time, so every formatted date follows with no wiring.
 * This component is therefore a pure trigger; it must never set `dir`, write
 * localStorage, or reload the page by hand.
 *
 * SEO: this is a client-side UI preference only. It changes no URL, route,
 * canonical, title, meta description or hreflang — `components/SEO.jsx` emits
 * the same static Arabic head tags whatever the visitor picked, and the
 * pre-rendered HTML from `scripts/generate-seo-html.js` is untouched.
 *
 * @param {'compact'|'full'} variant - `compact` is the desktop header: ONE
 *   button showing the current language, opening a menu of all of them. It
 *   was a row of chips until there were eight, which no header has room for.
 *   `full` is the phone menu: one row that opens the list in place.
 * @param {() => void} [onChanged] - Called after a successful switch (the
 *   mobile sheet uses it to close itself).
 */

/** 'ar-EG' → 'ar'. i18next can hand back a region-tagged value from navigator. */
function baseLanguage(language) {
  return (language || 'ar').toLowerCase().split(/[-_]/)[0];
}

const LanguageToggle = ({ variant = 'compact', onChanged }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  // i18next mutates its singleton without telling React. Mirror the active
  // language into state and drive it off the same `languageChanged` event the
  // direction handler uses, so every mounted toggle stays in sync.
  const [activeLanguage, setActiveLanguage] = useState(() => baseLanguage(i18n.language));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleLanguageChanged = (language) => setActiveLanguage(baseLanguage(language));
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, []);

  const selectLanguage = (languageCode) => {
    if (languageCode === activeLanguage) return;
    // Fires `languageChanged` → applyDocumentDirection() flips <html dir/lang>
    // → the detector caches the choice in localStorage.
    i18n.changeLanguage(languageCode);
    // Mirror to the backend so emails/push/OTP follow the same language.
    // Best-effort and authenticated-only: a guest has no server-side profile,
    // and firing it anyway would just add 401 noise to Sentry.
    if (isAuthenticated) {
      axios.put('/users/me/locale', { locale: languageCode })
        .catch(() => { /* silent — the UI already switched locally */ });
    }
    if (onChanged) onChanged();
  };

  // Both variants are mounted at the same time (each hidden by its own media
  // query in Layout), so the variant is part of the test id — otherwise a
  // `[data-testid=language-toggle-en]` selector resolves to two nodes.
  if (variant === 'compact') {
    const current = LANGUAGES.find((language) => language.code === activeLanguage)
      || LANGUAGES[0];
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={t('nav.language')}
          data-testid="language-toggle-compact"
          className="flex items-center gap-1 rounded-full border border-wefaqna-navy/20 bg-white/60 px-2.5 py-1 text-sm font-bold leading-none text-wefaqna-navy transition-colors hover:bg-wefaqna-navy/5"
        >
          <Languages className="h-4 w-4 text-wefaqna-navy/60" aria-hidden="true" />
          <span lang={current.code} dir={current.dir}>{current.shortLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 text-wefaqna-navy/60" aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[11rem]">
          {LANGUAGES.map((language) => {
            const isSelected = activeLanguage === language.code;
            return (
              <DropdownMenuItem
                key={language.code}
                onSelect={() => selectLanguage(language.code)}
                lang={language.code}
                dir={language.dir}
                aria-current={isSelected ? 'true' : undefined}
                data-testid={`language-toggle-compact-${language.code}`}
                className="justify-between gap-3 text-base"
              >
                <span>{language.nativeLabel}</span>
                {isSelected && <Check className="h-4 w-4 text-wefaqna-forest" aria-hidden="true" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // `full` - the phone menu. ONE row (globe, "Language", the current one)
  // that opens the list in place, the way Airbnb, Booking and Instagram do on
  // a phone. It was a grid of eight 48px buttons at the top of the menu: it
  // pushed the navigation below the fold and "Bahasa Indonesia" broke over two
  // lines. In place rather than a second popup over the sheet, and still
  // first in the menu, so a visitor who cannot read the Arabic labels finds
  // it without scanning past them. Every option keeps a 48px target.
  const current = LANGUAGES.find((language) => language.code === activeLanguage)
    || LANGUAGES[0];
  return (
    <div data-testid="language-toggle-full">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="language-toggle-full-list"
        data-testid="language-toggle-full-row"
        className="flex w-full min-h-[48px] items-center gap-3 rounded-lg px-3 text-wefaqna-navy hover:bg-wefaqna-subtle touch-manipulation"
      >
        <Languages className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="font-medium">{t('nav.language')}</span>
        <span className="ms-auto text-sm text-gray-600" lang={current.code} dir={current.dir}>
          {current.nativeLabel}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul id="language-toggle-full-list" className="mt-1 space-y-1" aria-label={t('nav.language')}>
          {LANGUAGES.map((language) => {
            const isSelected = activeLanguage === language.code;
            return (
              <li key={language.code}>
                {/* The ROW follows the menu's direction so every name lines
                    up on one edge; only the name carries its own language
                    and direction, which is what a screen reader needs. */}
                <button
                  type="button"
                  onClick={() => { selectLanguage(language.code); setOpen(false); }}
                  aria-current={isSelected ? 'true' : undefined}
                  data-testid={`language-toggle-full-${language.code}`}
                  className={`flex w-full min-h-[48px] items-center justify-between gap-3 rounded-lg px-4 text-start text-base touch-manipulation ${
                    isSelected
                      ? 'bg-wefaqna-subtle font-semibold text-wefaqna-forest'
                      : 'text-wefaqna-navy hover:bg-wefaqna-subtle'
                  }`}
                >
                  <span lang={language.code} dir={language.dir}>{language.nativeLabel}</span>
                  {isSelected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageToggle;
